import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { useDangerousRefResolution, useWheelScroll } from '#/components/hooks';
import {
  connectToOrderbookAsync,
  disconnectFromOrderbook,
  useDisplayedOrderbook,
} from '#/components/orders/orderBook/orderBookService';
import { useIsMobile } from '#/hooks/useIsMobile';

import { MAXIMUM_ZOOM_LEVEL } from './chartConfig';
import MarketDepthChart from './MarketDepthChart';
import ZoomControls from './ZoomControls';

const Wrap = styled.div`
  position: relative;
  height: 100%;
`;

const AbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
`;

const MarketDepthChartWrap = ({
  baseSymbol,
  quoteSymbol,
}: {
  baseSymbol?: string;
  quoteSymbol?: string;
}) => {
  const isMobile = useIsMobile();

  const marketPair =
    baseSymbol && quoteSymbol ? `${baseSymbol}-${quoteSymbol}` : undefined;

  // price:quantity sum pair
  const [bids, setBids] = useState<[number, number][] | undefined>();
  const [asks, setAsks] = useState<[number, number][] | undefined>();

  const [zoomLevel, setZoomLevel] = useState(0);

  const zoomIn = useCallback(() => {
    setZoomLevel(level => (level === MAXIMUM_ZOOM_LEVEL ? level : level + 1));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(level => (level === 0 ? 0 : level - 1));
  }, []);

  const zoom = zoomLevel / (MAXIMUM_ZOOM_LEVEL + 1);

  const bidsZoomed = useMemo(
    () => (zoom ? bids?.slice(0, Math.ceil(bids?.length * (1 - zoom))) : bids),
    [bids, zoom]
  );
  const asksZoomed = useMemo(
    () => (zoom ? asks?.slice(0, Math.ceil(asks?.length * (1 - zoom))) : asks),
    [asks, zoom]
  );

  const { serviceExists, serviceMarket } = useDisplayedOrderbook();

  useEffect(() => {
    if (!marketPair || !serviceExists || serviceMarket !== marketPair) {
      return;
    }
    const controller = new AbortController();
    connectToOrderbookAsync(marketPair, controller.signal, {
      type: 'depthChart',
      onChange: (responseAsks, responseBids) => {
        const asksRaw = responseAsks.map(ask => {
          const [price, amount] = ask;
          return [Number(price), Number(amount)] as [number, number];
        });
        const bidsRaw = responseBids.map(bid => {
          const [price, amount] = bid;
          return [Number(price), Number(amount)] as [number, number];
        });
        setBids(bidsRaw);
        setAsks(asksRaw);
      },
    });
    return () => {
      disconnectFromOrderbook('depthChart', controller);
    };
  }, [marketPair, serviceExists, serviceMarket]);

  const containerRef = useRef<HTMLDivElement>(null);
  const resolution = useDangerousRefResolution(containerRef);

  const handleScroll = useCallback(
    (up: boolean) => (up ? zoomIn() : zoomOut()),
    [zoomIn, zoomOut]
  );

  const { onWheel } = useWheelScroll(handleScroll);

  const midPrice = useMemo(
    () =>
      bidsZoomed &&
      asksZoomed &&
      bidsZoomed?.[0]?.length > 0 &&
      asksZoomed?.[0]?.length > 0
        ? (bidsZoomed[0][0] + asksZoomed[0][0]) / 2
        : undefined,
    [asksZoomed, bidsZoomed]
  );

  // console.log({ asks, bids, bidsZoomed, asksZoomed });

  if (!quoteSymbol || !baseSymbol || !asksZoomed || !bidsZoomed || !midPrice) {
    // Loading...
    return null;
  }

  return (
    <Wrap>
      <AbsoluteContainer
        ref={containerRef}
        onWheel={isMobile ? undefined : onWheel}
      >
        {resolution && (
          <MarketDepthChart
            bids={bidsZoomed}
            asks={asksZoomed}
            baseSymbol={baseSymbol}
            quoteSymbol={quoteSymbol}
            width={resolution.width}
            height={resolution.height}
            clickPrefillPrice={!isMobile}
          />
        )}
        <ZoomControls
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          isMaximum={zoomLevel === MAXIMUM_ZOOM_LEVEL}
          isMinimum={zoomLevel === 0}
          midPrice={midPrice}
          midPriceSymbol={quoteSymbol}
        />
      </AbsoluteContainer>
    </Wrap>
  );
};

export default MarketDepthChartWrap;
